import React from 'react';
import { Home, ChevronRight, Volume2, Heart, Mountain, Compass, Ship, BookOpen } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface GibbNewZealandSectionProps {
  onSelectPage?: (pageId: string) => void;
}

export const GibbNewZealandSection: React.FC<GibbNewZealandSectionProps> = ({ onSelectPage }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="space-y-10 font-sans text-[#2c221e]">
      {/* Sub-navigation Breadcrumbs & Voice Narrator Controls */}
      <header className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          {/* Breadcrumb: The Gibb Family > Page 2 of 2: Helen Gibb & The Gibb Family in NZ */}
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <button
              onClick={() => onSelectPage && onSelectPage('hub')}
              className="hover:underline flex items-center gap-1 font-bold text-[#7a5b3a]"
            >
              <Home className="w-3.5 h-3.5" />
              <span>The Gibb Family</span>
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />
            <button
              onClick={() => onSelectPage && onSelectPage('page1')}
              className="hover:underline text-[#7a5b3a]"
            >
              Page 1
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />
            <span className="bg-[#e8dec9] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider text-[#7a5b3a] text-[11px] sm:text-xs">
              Page 2 of 2: Helen Gibb &amp; Descendants
            </span>
          </nav>

          {/* Audio Narration Trigger */}
          <button
            onClick={speakCurrentPage}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs cursor-pointer"
            title="Listen to Helen Gibb & The Gibb Family"
          >
            <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
            <span>{isPlaying ? 'Listening to Section...' : 'Listen to this Chapter'}</span>
          </button>
        </div>

        <h1 className="font-serif text-3xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight leading-tight pt-1">
          HELEN GIBB (1839-1914) THE QUEEN OF CABBAGE TREE FLAT
        </h1>

        <div className="flex items-center gap-2 text-xs font-serif text-[#7a5b3a] uppercase tracking-wider font-semibold">
          <Mountain className="w-3.5 h-3.5" />
          <span>Page 2 of the 2-Part Gibb Family History</span>
        </div>
      </header>

      {/* Main Archival Text Content */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-10 text-base md:text-lg leading-relaxed text-[#2c221e]">
        
        {/* Section 1: Helen Gibb Introduction */}
        <section className="space-y-6">
          <p>
            Helen Gibb became a North Canterbury identity, making the most of opportunities and surviving the
            heartbreak of her husband's drowning when they had been married for less than four years.
          </p>

          <p>
            She was called "the Queen of Cabbage Tree Flat" (and no doubt other names by the surrounding
            community) and given a lot of support during her life there, from fellow Scots-born settlers and
            others who respected her hard work and efforts. She stood up for principle, was steadfast in her
            faith, prayed as she worked, said Grace before meals, held Bible Readings morning and night, was
            thrifty but charitable. Her strong religious belief and practice was well established. Her young
            brother was the Rev George Lindsay, and Helen was enormously proud of him and his achievements.
          </p>

          <p>
            She became the matriarch of her family and stood her ground when confronted with an issue. Not
            everyone got on with her but it seems she had universal respect. She was the Greta Valley
            postmistress for 19 years, a significant position of trust. Yet the many trees and long rows of oak
            trees<sup>1</sup> she planted along SH1 north of Greta Valley township are a silent everyday legacy of her
            efforts and continue to bring pleasure to travellers, birds and animals.
          </p>

          <p>
            But step back in time. Imagine a young Scottish couple living in beautiful valleys in the shadow of the
            Grampian Mountains in the 1850's. It was the homeland of the Lindsay clan, the Angus Glens, a
            series of tranquil valleys but then suffering from the Highland Clearances where landlords evicted
            tenant farmers to have more profitable sheep farming themselves. Every family had members who
            emigrated for better opportunities.
          </p>

          <p>
            Helen<sup>2</sup> was born on Wester Eggie farm (Heathfield), and lived at Glen Clova with her parents, Charles
            and Isabel (nee McAndrew) Lindsay, amidst the extended Lindsay family living. She had basic
            schooling and worked in the house and on the farm.
          </p>

          <p>
            Helen was betrothed to Stewart Lindsay Gibb, and had their son James Lindsay Gibb in 1858 when
            she was 19. Her granddaughter, Annie Denton (nee Gibb) always used to say, "in Scotland a
            betrothal was as good as a marriage." In 1860 Stewart emigrated to NZ, as a shepherd, departing
            from Bristol on 2 September 1860 on the Matoaka, arriving at Lyttelton on 1 December. There was
            plenty of work for shepherds on the sheep farms of North Canterbury and he went to Teviotdale just
            north of Amberley as head shepherd. It was to be three years before the couple were reunited.
          </p>

          <p>
            Helen left from Plymouth on 18 June 1863 on the Accrington. She travelled with her infant son,
            Stewart's brother, James Gibb (24), listed as a shepherd, and new sister-in-law, Mary (19). They
            arrived 75 days later in Lyttelton on 5 September 1863. It was smooth sailing until they rounded the
            Cape of Good Hope on the southern edge of Africa, when they faced gales and squally weather.
          </p>

          <p>
            The Accrington attracted much attention when it berthed, not merely for the excellent comforts it
            offered but more for the Normandy large water distilling apparatus that was able to supply plenty of
            fresh water to passengers throughout the journey. The ship was 85m long, weighed 1931 tonnes. By
            comparison, a Holland America cruise ship is 285m long and has 82,500 gross tonnage - about 40
            times the size. Captain Cook's Endeavour was 20m long and 366 tonnes.
          </p>

          {/* Footnotes 1 and 2 */}
          <div className="space-y-1.5 p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-4">
            <p><sup>1</sup> Front cover image</p>
            <p><sup>2</sup> Helen 1839-1914</p>
          </div>

          {/* Image 145: Accrington */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/145 Accrington that brought Helen Gibb to NZ.jpg"
              alt="Accrington that brought Helen Gibb to NZ"
              caption="Accrington"
              filenameHint="145 Accrington that brought Helen Gibb to NZ.jpg"
            />
          </div>

          <p>
            Accrington
          </p>

          <p>
            Just 12 days after she arrived Helen and Stewart married at Cameron's Hotel, Saltwater Creek<sup>3</sup> north
            of Kaiapoi, with their five-year-old son James Lindsay Gibb happily reunited with his young father. At
            that time Saltwater Creek was a tidal coastal port with two wharves, two hotels, two shops, a school
            and several businesses.
          </p>

          {/* Image 146: Cameron's Hotel */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/146 Cameron_s Hotel at Saltwater Creek.jpg"
              alt="Cameron's Hotel at Saltwater Creek"
              caption="Cameron's Hotel was towards the coast from the present bridge. (Photo: Kowai Archives Society) Drawing of Saltwater Creek when it was a busy town, before the railway went to Seton and the river silted up."
              filenameHint="146 Cameron_s Hotel at Saltwater Creek.jpg"
            />
          </div>

          <p>
            Cameron's Hotel was towards the coast from the present bridge. (Photo: Kowai Archives Society)
            Drawing of Saltwater Creek when it was a busy town, before the railway went to Seton and the river silted up.
          </p>

          <p>
            It is quite likely that Stewart knew Donald Cameron. They both came to NZ as shepherds from
            Scotland, although Cameron came out in 1850 and worked for seven years before settling at
            Saltwater Creek where he ran a store, hotel and the post office for 27 years.
          </p>

          <p>
            Stewart and Helen worked as the married couple on Teviotdale for 3.5 years, saving enough to buy
            24.5 ha at Cabbage Tree Flat just north of the turnoff to Motunau. They had two more sons. In 1864
            Stewart Burnett was born in a sod cottage by Brown's Bridge as Helen had set off for Amberley to
            give birth - on horseback - but she had left it too late! Stewart helped with the birth, no doubt using
            some of his expertise in lambing ewes! George was born at Teviotdale in November 1865. When
            Helen was pregnant with her fourth child Stewart drowned in the Motunau River, in March 1867, so
            he never saw his daughter, Helen Isabella (Ella).
          </p>

          <p>
            Helen told the story that in Scotland an old woman had predicted she would be widowed with three
            children. When she was pregnant with the fourth child she thought she had beaten the curse.
            Another version is that she had a dream. Widowed at 27, she took her family to the Cabbage Tree
            Flat farm, staying first with the Arkle family on Glen Allen and then getting a simple sod cottage built
            on her land. She planted an orchard, wrote home for gorse seeds to plant for fences, kept cows,
            sheep and pigs.
          </p>

          <p>
            Her neighbour William Acton-Adams tried to move the surveyed boundary of her small holding.
            Short, with wavy brown hair, resolute and determined, Helen faced him with her Bible in hand, and
            quoted scriptures regarding boundaries, widows and orphans. The boundary stayed at it was. The
            widow in the sod cottage stood up to the influential man.
          </p>

          <p>
            In the early 1870's the new road to Cheviot passed near her farm. She had already been providing
            refreshments to travellers but this opportunity meant she could set up an Accommodation House
            with paddocks for drovers to overnight their sheep. Coaches could stop and change horses. For
            many years it was the only catered stopover between Amberley and Cheviot. This enterprising
            woman made the most of it. By 1882 she had bought another 35ha adjoining Cabbage Tree Flat,
            which her sons were able to farm as they grew up.
          </p>

          {/* Footnotes 3 and 4 */}
          <div className="space-y-1.5 p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-4">
            <p>
              <sup>3</sup> Saltwater Creek was developed in 1858 by Christopher Dampier, school and teacher's house opened 1866,
              was busy until port began to silt up and the railway bypassed town so many businesses shifted to closest
              railway town, Sefton.
            </p>
            <p>
              <sup>4</sup> Brown's Bridge was a track covered in cut and layered manuka scrub through a swampy patch north of
              Amberley, with an accommodation house nearby
            </p>
          </div>

          {/* Image 147: Mail coach arriving at Amberley */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/147 Mail coach arriving at Amberley.jpg"
              alt="Mail coach arriving at Amberley"
              caption="Most of the mail delivered to post offices arrived on horseback, by coach or mail cart. This shows the stage coach at Amberley."
              filenameHint="147 Mail coach arriving at Amberley.jpg"
            />
          </div>

          <p>
            Most of the mail delivered to post offices arrived on horseback, by coach or mail cart. This shows the stage
            coach at Amberley.
          </p>

          <p>
            In 1883 Helen was appointed the Greta Valley Postmistress, a post she kept until 1899. There was no
            rural delivery service until 1905 so everyone had to go to the post office to get their mail, postal
            notes, money orders, register births, deaths and access many other services. She would have
            witnessed statutory declarations and advised on government services.
          </p>

          <p>
            In 1880 there were 856 post offices in NZ. NZ was one of the first countries in the world to issue
            stamps with images of the landscape, birds and animals.
          </p>

          <p>
            Helen's home was the focus of community activities for most of her life. Presbyterian church services
            were held in her home until the Greta Valley School was built in 1893. Baptisms were held there. Her
            third son George Lindsay was baptised at his home by Rev Rapley, a Wesleyan minister from
            Amberley.
          </p>

          <p>
            She was a great knitter, knitting marl-red socks for her sons on four steel knitting needles, and black
            socks for herself and Ella. She used the Scottish custom of pushing the spare needle into a ball of
            wing feathers from a fowl, tucked into the side of her belt. Towards the end of her life she knitted
            herself a pair of knee-length white stockings with instructions to Ella that they were to be put on her
            legs when she died. When her granddaughter Annie Gibb (aged 95) awoke in Christchurch Public
            Hospital after a hip replacement following a fall, she looked down at the white pressure stockings
            that had been put on her legs and she exclaimed, "oh, I am dead" to the understanding amusement
            of those in the room.
          </p>

          <p>
            Ella was also an excellent knitter, making all the curtains for their rooms using a thick white cotton.
            She always knitted the same loose open pattern and proper knitting needles, from about 1885.
            Annie's mother-in-law, Eliza Denton, knitted curtains and double bedspreads for their first sod
            house, about 1896. Helen taught her granddaughter, Annie Gibb to knit in 1907. Annie knitted socks
            for her husband, many clothes for her five children, then babywear for grandchildren. In her late
            90's she was still knitting, making a bassinette cover in cream coloured 3 ply wool, and many baby
            singlets in rib and stocking stitch.
          </p>

          <p>
            Before Christmas 1894 five rooms and a passage were added to Helen's house. "Four rooms and a
            verandah formed the front of the house. Mrs Gibb's bedroom was at the east end, and behind it and
            joining to it was George's bedroom. Next to Mrs Gibb's bedroom was the kitchen, then a passage
            connected to her old sitting room and three bedrooms with the front of the house. Next to the
            passage and joining the verandah was the dining room and at the west end of the new portion of the
            house was the best bedroom. Part of the west portion of the verandah was enclosed in glass to form
            a conservatory. The floor of the verandah was concrete...in case smokers dropped matches and
            started fires. There was a good deal of work required in joining up the new portion with the old house
            and Mr Harris was putting in claim for work done outside the contract agreement which Mrs Gibb
            resented, and she told him not to do any more extra work."<sup>5</sup>
          </p>

          <p>
            The hub of the district moved from Motunau to Scargill when the rail ink and station opened there in
            1886. Helen kept the Accommodation House going and boarded her grandchildren so they could
            walk to Greta Valley School. Her sons and their families were all nearby. Ella lived with her mother
            and worked in the house until Helen died in July 1914.
          </p>

          {/* Footnote 5 */}
          <div className="p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-4">
            <p><sup>5</sup> From A Personal History by Edward Roberts page 23</p>
          </div>

          {/* Image 148: Helen with James, George, Stewart and Ella */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/148 Helen with James, George, Stewart and Ella at Cabbage tree Flat.jpg"
              alt="Helen with James, George, Stewart and Ella at Cabbage tree Flat"
              caption="Helen (seated) and Ella with James, George and Stewart Gibb behind, in front of the Cabbage Tree Flat Accommodation House."
              filenameHint="148 Helen with James, George, Stewart and Ella at Cabbage tree Flat.jpg"
            />
          </div>

          <p>
            Helen (seated) and Ella with James, George and Stewart Gibb behind, in front of the Cabbage Tree Flat
            Accommodation House.
          </p>

          <p>
            Edward Roberts had known Helen most of his life and had lived and worked for her at various
            times. He said the Rev George Lindsay was at the funeral on 28 July...."my mother and father came
            by train from Christchurch to attend....of course most of the residents of around Motunau and some
            from Scargill were there. After the funeral some of the people were discussing the trouble over the
            murder of the heir to the Austrian throne and wondering if it would lead to a general war in
            Europe." And of course, it did and the impact would be felt in North Canterbury with the enlistment,
            death and casualties of the many fine young men.
          </p>

          <p>
            In his memoir Edward made several mentions of Helen.
          </p>

          <blockquote className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif italic text-base sm:text-lg text-[#2c221e] shadow-xs space-y-4">
            <p>
              "Toward the end of 1894 Mrs Gibb asked her son (Jimmy) to let me go and live with her and
              attend school from her place. I would be useful to her to assist her daughter."
            </p>
            <p>
              When she got congestion of the lungs "she consulted a doctor who told her to get some
              decayed teeth removed and her health would improve. She often told the story in my
              hearing. It included the words "I suppose I was very foolish as I took a hammer and knocked
              them out, breaking my jaw as I did so."
            </p>
            <p>
              "In telling a story she always spoke in a low clear voice.....She was a Lindsay and I heard her
              say her great grandfather was Lord Lindsay and her grandfather a cousin of Lord Lindsay, but
              there was no money in her parent's family."
            </p>
            <p>
              At the high tea and concert celebrations at the opening of the Greta Valley School in 1893,
              Herbert Acton-Adams had asked Mrs Gibb to supply some of those attending with an
              evening meal, which she prepared, but when she arrived he demanded she pay a shilling for
              entry. She resented his demand and said she would not go in if she had to pay and she
              considered she was doing her share in providing and serving an evening meal without having
              to pay to get in to do so - she went in and several ministers of religion sat at her table in
              solidarity with her. The event was also notable for being the first-time most of them had
              seen pictures on screen by a magic lantern. The evening finished with a dance in the Tipapa
              woolshed
            </p>
            <p>
              In another episode, in the autumn of 1893, Acton-Adams built an accommodation house
              near the junction with Motunau Beach Road and rented it to his former manager (Malcolm
              McInnes) who then applied for a Publican's Licence. The Amberley police office opposed the
              application - Constable Maurice Roach said Mrs Gibb had for years supplied accommodation
              to the travelling public, including hard-up swaggers, and a licenced hotel was unnecessary.
            </p>
            <p>
              Helen Gibb as postmistress 10 received telegrams and had them delivered, at sixpence each.
              She gave threepence to the delivery boy, who was often young Edward Roberts. Acton-
              Adams at Tipapa frequently got telegrams "and it was not long before he began to think he
              should have telegrams delivered free of cost and said Tipapa was under a mile from the post
              office. George Gibb and I measured the distance with a surveyor's chain and found the
              distance to be 90 chains<sup>11</sup>. Not to be beaten for free delivery, Acton-Adams put 2 gates in
              the fence in a triangle paddock that lay between the post office and Tipapa so that the
              telegrams could be delivered across the paddock on a shorter distance from the post office;
              but after full enquiry from the postal department it was found that the public road was the
              legal way for delivery, and Acton-Adams had to pay delivery on the telegrams he received
              while the dispute was unsettled."<sup>12</sup>
            </p>
          </blockquote>

          {/* Footnotes 6 through 12 */}
          <div className="space-y-1.5 p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-4">
            <p><sup>6</sup> His two sisters, Lizzie and Jennie, married Jim and Stewart Gibb, Helen's two eldest sons.</p>
            <p><sup>7</sup> Robert Morris and Sarah Elizabeth Roberts</p>
            <p><sup>8</sup> Page 24 of Edward's memoir</p>
            <p><sup>9</sup> Magic lantern was an early type of projector that used images on glass plates, a very early version of the slide projector</p>
            <p><sup>10</sup> She retired from the post office in 1899, after 19 years in the job</p>
            <p><sup>11</sup> 1 chain =20.11m</p>
            <p><sup>12</sup> Edward Robert's Memoir pp 29-30</p>
          </div>

          <p>
            T.E.L. Roberts in his book "Motunau" made these mentions of Helen Gibb.
          </p>

          <blockquote className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif italic text-base sm:text-lg text-[#2c221e] shadow-xs space-y-4">
            <p>
              Mrs Gibb, as a woman in mid-life, mounted on a prancing steed, riding to Amberley and
              returning the same day, and arriving back fit and well. Dancer was a thick set steed, pulling
              hard most of the time, but no horse for a novice to venture on.
            </p>
            <p>
              Her second son, Stewart, was a great gardener, and on one occasion had some fine carrots
              with which he wished to shame his mother, and so took them off for exhibition. When he
              arrived at the entrance gate, to his chagrin there were, actually lying at the gate, a bundle of
              carrots which eclipsed his, and as quick as a thought he substituted hers for his, and went on
              in, but alas, Helen was watching him through the window and he did not get away with his
              attempted ruse!
            </p>
          </blockquote>

          {/* Image 149: Helen Gibb and brother Rev George Lindsay */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/149 Helen Gibb and her brother Rev George Lindsay with wider family 1907.jpg"
              alt="Helen Gibb and her brother Rev George Lindsay with wider family 1907"
              caption="Photo taken by roadside opposite Cabbage tree Flat, 1907 or 1908 (Photo: R.M. Roberts). Back-R: Stewart Burnett, James Lindsay and George Gibb (sons of Helen), Burnie and Morris. Middle L-R: Helen Gibb, her brother the Rev George Lindsay, and her daughter Ella. Front L-R: Unnamed, Helen, Francis, Jane and baby Alice, George, Annie, Lina and Mildred (daughter of George Gibb). Italics - children of Eliza (nee Roberts) and James Gibb. Blue children of Jane (nee Roberts) and Stewart Gibb."
              filenameHint="149 Helen Gibb and her brother Rev George Lindsay with wider family 1907.jpg"
            />
          </div>

          <p>
            Photo taken by roadside opposite Cabbage tree Flat, 1907 or 1908 (Photo: R.M. Roberts)
            Back-R: Stewart Burnett, James Lindsay and George Gibb (sons of Helen), Burnie and Morris
            Middle L-R: Helen Gibb, her brother the Rev George Lindsay, and her daughter Ella
            Front L-R: Unnamed, Helen, Francis, Jane and baby Alice, George, Annie, Lina and Mildred (daughter
            of George Gibb)
            Italics - children of Eliza (nee Roberts) and James Gibb
            Blue children of Jane (nee Roberts) and Stewart Gibb
            Those trees are also in the photograph of James Gibb's horse team.
          </p>
        </section>

        {/* Section 2: Taken From Motunau Memories */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            TAKEN FROM "MOTUNAU MEMORIES" by T.E.L. Roberts (written in the 1940's)
          </h2>

          <blockquote className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif italic text-base sm:text-lg text-[#2c221e] shadow-xs space-y-4">
            <p>
              "At the end of the 1850s two brothers of the name of Gibb arrived in NZ and later played a great part
              of the district of Motunau, where their name still flourishes in an honoured way. James Gibb found
              employment with Caverhill and Stewart Gibb on Teviotdale."
            </p>
          </blockquote>

          <p>
            Helen Lindsay shortly followed and she and Stewart Gibb were married at Saltwater Creek and took
            up residence at The Slip on Teviotdale. They lived there until the end of 1856 when Stewart Gibb went
            to Motunau Station to do some valuing and collect some straggler sheep. The night before going to
            Motunau he spent with Jacksons at the Cave Hut<sup>13</sup>. The next day he and Robert Struthers left Motunau
            Station for the river, crossed the swing bridge, and went down to the river. Stewart drowned <sup>14</sup> - his
            body was later taken to Leithfield by boat and buried there. T.E.L. Roberts recorded that the two men
            who rowed his body down were Long Dick and a Maori.
          </p>

          <blockquote className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif italic text-base sm:text-lg text-[#2c221e] shadow-xs space-y-4">
            <p>
              "It was a tragic story for the young widow, and a singular part of it all was that she had previously
              seen herself left a widow with three children, in a dream vision. When she conceived the fourth child
              she rejoiced, because she felt then her dream was false, but alas it was too true. Following on her
              husband's death he appeared to her in a vision and told her to go to the land he had bought at Cabbage
              Tree Flat and make her home there. This she did in 1867, taken there by contractor James Barr, going
              via Waikari Valley to Greta Peaks."
            </p>
          </blockquote>

          <p>
            There was a flood in 1868, with the swamp in full flood. Then an earthquake - Helen Gibb rushed
            outside to chase away the beast she thought must have caused the shaking by rubbing against the sod
            cottage. The sod cottage she lived in first had the floor at ground level, making poor drainage. Helen
            developed rheumatic fever in the damp conditions and her doctor advised her to get a wooden house
            built to replace the sod cottage. Much of the money and timber was gifted to Helen from neighbours,
            particularly John Caverhill of Motunau Station.
          </p>

          {/* Image 150: Sod cottage */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/150 Example of a sod cottage built on the ground.jpg"
              alt="Example of an 1860's sod cottage"
              caption="Example of an 1860's sod cottage - note it is at ground level"
              filenameHint="150 Example of a sod cottage built on the ground.jpg"
            />
          </div>

          <p>
            Example of an 1860's sod cottage - note it is at ground level
          </p>

          <p>
            From her first days at Cabbage Tree Flat Helen had provided meals and accommodation to the
            travelling public, and she sent butter and milk to others, such as the surveying parties camping in the
            area. Usually her eldest son, Jimmy, delivered the parcels. He also made sure the travelling parties
            took the right routes on their onward journeys. This he did to Captain Gibson, the Lyttelton Harbour
            master who suggested they plant trees, and later sent them a bundle of poplar and willow cuttings -
            they grew into the Lombardy and willows still growing in the Greta Valley area.
          </p>

          <p>
            The Hon W. Robinson<sup>15</sup> gifted her fruit trees, telling her to go to the Jouberts and Twopenny's
            Exhibition in Christchurch<sup>16</sup> and book up a range of trees on his account.
          </p>

          <p>
            Helen also planted the oak trees along SH1, pictured on the front cover of this booklet.
          </p>

          <p>
            Helen won the admiration of her community for her many gutsy efforts. At the end of the 1860s when
            telegraph poles were being replaced, Helen made application for some of the poles, which were free
            to a certain number. Transport was costly, so Helen borrowed a horse called Sogera from Jack Price at
            Greta Peaks, a veteran of the Crimean War. "She had harness and chains, and after being shown how
            to attach the snigging chain to the poles, Mrs Gibb and Jimmy the son did quite well. There were many
            such incidents in the life of this enterprising venturesome woman, which went to make for her success."
          </p>

          {/* Footnotes 13 through 16 */}
          <div className="space-y-1.5 p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-4">
            <p><sup>13</sup> Corner of Motunau Beach Rd and Heathfield Rd.</p>
            <p><sup>14</sup> This is written about in the chapter The Drowning at Motunau</p>
            <p><sup>15</sup> William Robinson (1814-1889) called "Ready Money Robinson", owned Cheviot Hills, politician and race horse owner</p>
            <p><sup>16</sup> April - July 1882 in South Hagley Park, more than 250 people visited</p>
          </div>

          <p>
            In the 1870's cuttings were formed through the gullies from Waipara to Cheviot, thus diverting traffic
            past Helen's Accommodation House, and giving her a solid business opportunity. It was about 40 Km
            from Leithfield to Cabbage Tree Flat, and 32 Km to Cheviot. Horses were changed and let out to graze
            and rest.
          </p>

          <p>
            Helen employed a young lad to assist with the horses on these stopovers and changes, Ashleigh Lewis.
            TEL Roberts called him a remarkable rider for his age and fearless to a point of recklessness.
            "One of his duties was to have these horses in hand, and at the appointed hour Ashleigh might be seen
            coming at a gallop, sometimes even without saddle or bridle. He recently told me (as TEL wrote) that
            one an occasion coming at a gallop, no saddle or bridle, when his mount lurched violently, sending
            Ashleigh off quarter bow, to follow on foot to the home paddock, here the horses were making," TEL
            wrote.
          </p>

          <p>
            "To meet the continual calls of the travelling public, ham and eggs played a useful part for the table,
            and to that end Mrs Gibb kept a considerable number of pigs. Those pigs were under Ashleigh's charge
            at periods every day, to be taken out to one of the paddocks to graze. Ashleigh soon discovered there
            was endless fun to be had riding the bigger pigs, and he soon had apt pupils in the boy members of our
            family.
          </p>

          {/* Image 151: Helen kept Berkshire pigs */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/151 Helen kept Berkshire pigs.jpg"
              alt="Helen kept Berkshire pigs"
              caption="Helen kept Berkshire pigs"
              filenameHint="151 Helen kept Berkshire pigs.jpg"
            />
          </div>

          <p>
            Let it be well understood, there was a good deal of discretion and a high gorse fence between us and
            mine hostess so that our secret was well kept. We actually rode the very hair off selected steeds, and
            the thrill of it has never been forgotten. On one occasion, the creek, over which the pigs had to go, was
            in pretty high flood, and Ashleigh decided it would be great sport to swim the stream. As his mount
            drew near Ashleigh hopped on and piggy never hesitated, but took the flood as a matter of course, and
            as the launching took place, the stern, due to Ashleigh's weight, submerged til Ashleigh's arms were
            awash, at which he made a wild leap at a willow branch, cancelling his projected trip, and emerged
            somewhat drenched and bedraggled."
          </p>

          <p>
            TEL also wrote about how they got the plaster to make chimneys and coat their houses with, in what
            he said was the first lime building in North Canterbury. They built a lime kiln where there was a lot of
            loose limestone and manuka, and where there were little mounds in the ground making it a good site.
            "One mound was selected and the kiln dug about 3m deep, and then to give access to the bottom of
            the kiln the side of the mound was squared down leaving a short wall for the kiln, through which a hole
            was dug to allow the fire to be kindled at the bottom. The kiln dug was about the size of a well...first a
            goodly supply of kindling wood to ensure a good light up, then a layer of small limestones, then another
            layer of wood until the kiln was filled. All in readiness the match was applied and all went as merrily as
            a wedding bell. When we herd the fire roaring nicely we packed our traps on a sledge and returned to
            Glendhu, about 3.5 km away."
          </p>

          {/* Image 152: Lime kiln */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/152 lime kiln near Greta valley.jpg"
              alt="Lime kiln near Greta Valley"
              caption="Lime kiln near Greta Valley"
              filenameHint="152 lime kiln near Greta valley.jpg"
            />
          </div>

          <p>
            They returned three days later, and found the lime was of first-class order. It was taken by sledge to
            Happy Valley where the Roberts house was being built.
          </p>

          {/* Image 153: Early map of Greta Valley */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/153 Early map of Greta Valley.jpg"
              alt="An early map of Cabbage Tree Flat"
              caption="An early map of Cabbage Tree Flat"
              filenameHint="153 Early map of Greta Valley.jpg"
            />
          </div>

          <p>
            An early map of Cabbage Tree Flat
          </p>
        </section>

        {/* Section 3: Cabbage Tree Flat Name Origin & Descendants */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            WHERE DID THE NAME CABBAGE TREE FLAT COME FROM?
          </h2>

          <p>
            There was a forest of cabbage trees just east of the site of the Tipapa homestead a literal jungle of
            toi, toi, flax and other indigenous plants, "studded with endless cabbage trees" according to TEL
            Roberts.
          </p>

          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e] pt-4">
            HELEN'S DESCENDENTS - her family grew considerably with the next generations
          </h2>

          <h3 className="font-serif text-xl md:text-2xl font-bold text-[#7a5b3a]">
            JAMES LINDSAY GIBB (1858-1930) - THE FIRST-BORN SON farmed at Heathfield on Heathfield Road, off Motunau Beach Road
          </h3>

          <p>
            James, or Jim and Jimmy, as he was called, bought Heathfield in the 1880s, about the same time that
            his mother bought another block adjoining Glen Clova. That required a ruse, as described by Edward
            Roberts. <sup>17</sup>
          </p>

          {/* Image 153a: Gibb Train */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/153a Gibb Train.jpg"
              alt="Gibb Train Journey to Land Office"
              caption="Train journey to Christchurch Land Office"
              filenameHint="153a Gibb Train.jpg"
            />
          </div>

          <blockquote className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif italic text-base sm:text-lg text-[#2c221e] shadow-xs space-y-4">
            <p>
              "James was to proceed to Christchurch to put the deal into effect and took the train for Christchurch
              from Amberley. It so happened that another buyer for the same piece of land also got on board the
              train, which made James do some thinking. All went according to schedule till Papanui was reached
              and the train was ready to steam out again. When it was actually in motion James stepped off, and
              took the horse tram to Christchurch, going straight to the Land Office, secured the maps and had just
              completed the purchase when in walks his opponent."
            </p>
          </blockquote>

          <p>
            T.E.L. wrote that James spent his time fencing and getting his holding into shape. "Being a bachelor,
            his own quarters were of a more modest order, but the writer spent pleasant days with J.L. on his
            then lonely ranch. He had in a field of turnips to which wild pigs came down in hordes at night when
            they were t the easting stage and to try and kill them off J.L resorted to strychnine poisoning. We
            placed meat bats around the field and although they were all taken we never found a dead pig, nor
            did they appear to diminish in numbers."
          </p>

          <p>
            Jim started shearing in his early teens. He was competitive, quickly becoming the head shearer and
            reportedly could shear 50 sheep before breakfast in the Glenmark woolshed. He did at least one
            shearing stint in Australia.
          </p>

          <p>
            T.E.L. again: "A thumb-nail sketch of Jim, given me by one of his associates of Horsley Downs, in the
            palmy days of that station, showed him on a Saturday evening, immaculately dressed, riding
            breeches and boots, also white gloves, mounted on a prancing steed, for Jim would ride nothing but
            the best, and the best could not throw him in a test of horsemanship."
          </p>

          {/* Image 154: James Gibb */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/154 James Gibb.jpg"
              alt="James Gibb"
              caption="Jim Gibb, kind to young people, made friends easily and kept them for his lifetime"
              filenameHint="154 James Gibb.jpg"
            />
          </div>

          <p>
            Jim Gibb, kind to young people, made friends easily and kept them for his lifetime
          </p>

          <p>
            Jim gave up shearing in his early twenties. He began making a lot of visits to the Happy Valley home
            of Robert and Sarah Roberts, where their eldest daughter Lizzie (Eliza Catherine) was pleased to see
            him and accepted his hand in marriage, getting married there on the silver wedding anniversary of
            her parents. Much effort was made to get the Rev Campbell from Waiau to Happy Valley for the
            ceremony.
          </p>

          <h4 className="font-serif text-lg font-bold text-[#2c221e]">
            Romance in the making - from Edward Robert's "A Personal History"
          </h4>

          <blockquote className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif italic text-base sm:text-lg text-[#2c221e] shadow-xs space-y-4">
            <p>
              "About 1886 John McLelland got an idea my sister Izzie would make him a suitable wife, and tried his
              luck at courting. Mother always seemed to be in the way, and John informed her that young people
              required a little privacy to talk of their affairs. However, the opportunity came later when Lizzie and
              some of my brothers went for a picnic to the Glendhu Bush and had to pass the Glendhu home. On
              the return from the bush Lizzie took a short cut down a spur to the road while the boys drove the
              horse by a round-about way. John McLelland happened to see Lizzie on her own, and seized the
              opportunity he had been waiting for, and set off after her. When he thought he was within hearing
              distance he called to her: "Hold on Miss Lizzie, I have an object in view." As he did not get a
              satisfactory answer to his proposal he said: "Miss Lizzie, you will go through the orchard and pick a
              crab."
            </p>
            <p>
              About this time Jim Gibb came on the scene, and one Sunday were together when they saw John
              approaching on Fairy, his wiry little hack. Jim thought it was a good opportunity to make a little
              progress with Lizzie and suggested she take his arm to see how John would react.
            </p>
            <p>
              Some of my brothers happened to be in the vicinity and John remarked to them "you could not
              separate the pair with a man on horseback with a stock whip.
            </p>
            <p>
              Jim Gibb was about 28 and Lizzie about 17. The courtship lasted about five years and ended in a
              marriage at Happy Valley on Christmas Eve 1891."
            </p>
          </blockquote>

          <p>
            James and Eliza Gibb had four children:
          </p>

          {/* 4 children list */}
          <div className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif space-y-3 text-base sm:text-lg">
            <p>
              • <strong>Helen Isabel</strong> married Fred Cole and farmed at Waiau, and had two daughters - Edna and Meryl.
            </p>
            <p>
              • <strong>Margaret Annie</strong> married Tommy Denton and farmed at Motunau and then Oakdale at
              Southbridge, and they had five children - Harry, Isabel, Warren, Margaret and Heather.
            </p>
            <p>
              • <strong>Robert Morris Stewart</strong> took over Heathfield but later sold the property. He married
              Dorothy Elizabeth Drake (later they divorced) and had one son, Douglas. Dorothy later
              died of a brain tumour. Morris remarried later in life. Doug, from the age of 12, went to
              Oakdale to live with the Denton family until he did an apprenticeshiop as a marine
              engineer and worked on ships and engines most of his life. Dorothy's mother had been
              housekpeeper for Jimmy Gibb. Somehow, he signed a paper saying he would be
              responbsible for Drake debts and they added up to a considerable sum and necessitated
              the sale of the farm, savagely disappointing his sisters.
            </p>
            <p>
              • <strong>Lina</strong> (Catherine Lindsay) died aged 26.
            </p>
          </div>

          {/* Footnote 17 */}
          <div className="p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-4">
            <p><sup>17</sup> From "Motunau Memories" 1943</p>
          </div>

          {/* Image 155 & 156: Annie and father Jim & Lizzie and James Gibb family */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            <ArchivalImageCard
              src="/images/155 James Gibb and daughter Annie about 1918.jpg"
              alt="James Gibb and daughter Annie about 1918"
              caption="Annie and her father, Jim"
              filenameHint="155 James Gibb and daughter Annie about 1918.jpg"
            />
            <ArchivalImageCard
              src="/images/156 Lizzie and James Gibb with Helen, Morris, Annie and Lina about 1912.jpg"
              alt="Lizzie and James Gibb with Helen, Morris, Annie and Lina about 1912"
              caption="Back: L-R: Helen, Morris and Annie. Front: LR: Lizzie (1869-1914), Lina and Jim Gibb"
              filenameHint="156 Lizzie and James Gibb with Helen, Morris, Annie and Lina about 1912.jpg"
            />
          </div>

          <p>
            Annie and her father, Jim
          </p>

          <p>
            Back: L-R: Helen, Morris and Annie. Front: LR: Lizzie (1869-1914), Lina and Jim Gibb
          </p>
        </section>

        {/* Section 4: Stewart Burnett Gibb */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h3 className="font-serif text-xl md:text-2xl font-bold text-[#7a5b3a]">
            STEWART BURNETT GIBB (1864-1948) - THE SECOND SON<sup>18</sup>
          </h3>

          {/* Image 157: Stewart Burnett Gibb */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/157 Stewart Burnett Gibb.jpg"
              alt="Stewart Burnett Gibb"
              caption="Stewart Gibb, appointed a Justice of the Peace in later life, pictured in 1893"
              filenameHint="157 Stewart Burnett Gibb.jpg"
            />
          </div>

          <p>
            Stewart Gibb, appointed a Justice of the Peace in later life, pictured in 1893
          </p>

          <p>
            Stewart went to work as a shepherd on Motunau Station<sup>19</sup> from 1881-1890 so it was a short trip
            for him to visit the Roberts family, and their second daughter Jane<sup>20</sup>, called Jenny in her family.
            Stewart and Jenny got to know each other in their rambles over the hills and gullies according to
            Jenny's younger brother T.E.L. who carefully observed his older siblings.
          </p>

          <p>
            Jenny was good friends with Annie Arkle and they would often go to the orchard and spend time
            there, on swings attached to the trees, or they would stroll down to Grandmother Arkle's grave in a
            hollow in the little hill. They had a little plot of flowers there, and a treasured rose, with seashells
            and coloured beach stones. No doubt they discussed the bachelors of the countryside, and proximity
            may have helped Stewart because he married Jane (22) in 1893, at Heathfield.
          </p>

          <p>
            On Motunau Station it was a period of development, with extensive fencing, ploughing for
            cultivation and growing wheat under H. J. Hall, who had bought the station in 1881 from the Bank of
            New Zealand who had taken over the deeds in 1879. It was running 18,000 sheep at that time. The
            top half of the station was sold to William Acton-Adams and called Tipapa. Supplies came in to
            Motunau by small coastal trading boats which were essential before the inland roads were formed.
          </p>

          <p>
            It was a good time for Stewart to learn about different breeds of sheep, careful property
            management, and how to develop good land for arable production. He was head shepherd at Tipapa
            for about two years after Acton-Adams bought it and his wife Jenny worked for the manager's wife,
            Mrs Ashton Wachsmann<sup>21</sup>. She cooked the meals for the men on the station, who ate in the kitchen.
            They later settled on Glen Clova and had their large family, growing up close to their other Gibb and
            Roberts cousins.
          </p>

          <p>
            In 1890 Stewart and George won a ballot for a grazing run when Greta Peaks station was divided into
            four. About 11 years later they dissolved that partnership -Stewart stayed on Glen Clova and George
            took over the freehold land they had bought from Greta Peaks (Craigknock).
          </p>

          <p>
            Stewart Burnett Gibb married Jane Roberts, and their children were
          </p>

          {/* Children of Stewart & Jane */}
          <div className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif space-y-2 text-base sm:text-lg">
            <p>• <strong>Bernard Stewart</strong> called Burnie (1894-1917) killed on Messines Ridge, Flanders in WW1</p>
            <p>• <strong>Mildred Clara</strong> (1896-1980) married George Richard Tarbotton, farmer of Ashburton district</p>
            <p>• <strong>Francis Helen</strong> (1897-1986) married James Lennie</p>
            <p>• <strong>Annie Margaret</strong> (born 1899) married William Elliott of Greta Valley</p>
            <p>• <strong>Edward Lindsay</strong> known as Ted (1910-2002) married Moira Conyard Hamilton in 1938</p>
            <p>• <strong>Greta</strong> (1902-1988) married Leonard Stockley Aroa</p>
            <p>• <strong>Olive</strong> (born 1912)</p>
            <p>• <strong>Winifred Alice</strong> (born 1907) married Duncan Brown</p>
          </div>

          {/* Footnotes 18 through 21 */}
          <div className="space-y-1.5 p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-4">
            <p><sup>18</sup> 1865-1948</p>
            <p><sup>19</sup> Motunau Station was the oldest run in Canterbury outside Deans Brothers at Riccarton, dated from 1847</p>
            <p><sup>20</sup> Jane Gibb 1871-11954</p>
            <p><sup>21</sup> Wachsmann had been manager of Motunau Station and had married a daughter of the owner, TJ Hall</p>
          </div>

          {/* Images 158 & 159: Glen Clova 1899 and Glen Clova pre 1945 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            <ArchivalImageCard
              src="/images/158 Glen Clova 1899.jpg"
              alt="Glen Clova 1899"
              caption="Glen Clova with Davaar Hills in background, 1899 where Stewart and Jane Gibb farmed (Photo: R.M.Roberts)"
              filenameHint="158 Glen Clova 1899.jpg"
            />
            <ArchivalImageCard
              src="/images/159 Glen Clova pre 1945.jpg"
              alt="Glen Clova pre 1945"
              caption="Glen Clova about 1945 (Photo: R.M. Roberts)"
              filenameHint="159 Glen Clova pre 1945.jpg"
            />
          </div>

          <p>
            Glen Clova with Davaar Hills in background, 1899 where Stewart and Jane Gibb farmed (Photo:
            R.M.Roberts)
          </p>

          <p>
            Glen Clova about 1945 (Photo: R.M. Roberts)
          </p>
        </section>

        {/* Section 5: George Gibb */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h3 className="font-serif text-xl md:text-2xl font-bold text-[#7a5b3a]">
            GEORGE GIBB (1865-1949)- THE THIRD SON<sup>22</sup>
          </h3>

          {/* Image 160: George Gibb */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/160 George Gibb.jpg"
              alt="George Gibb"
              caption="George Gibb (1865-1949)"
              filenameHint="160 George Gibb.jpg"
            />
          </div>

          <p>
            George grew up at Cabbage Tree Flat although he had two years, with his brother Stewart, living at
            Waimate with his uncle Rev George Lindsay, and attending school there.
          </p>

          <p>
            George lived much of his adult life as an essential part of life on Cabbage Tree Flat. He was
            considered to be a great judge of cattle. T.E.L. Roberts wrote that it was a nicely self-contained farm,
            as in the earlier days it had to meet the requirements of the travelling public. Hay and oats were
            grown for cattle and horses, the chaff being cut by the same horsepower chaffcutter that remained
            always in the same place in the stockyard. Usually in autumn George would drive the cattle to the
            Rangiora saleyards.
          </p>

          <p>
            He was a great friend of Edward Roberts so there are a lot of recollections about him from that man
            with such a wonderful memory. They shared a bedroom while Edward lived with his grandmother to
            go to Great Valley School.
          </p>

          <blockquote className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif italic text-base sm:text-lg text-[#2c221e] shadow-xs space-y-4">
            <p>
              For example: On Christmas Day 1884 George Gibb, then 19, drove our family to a picnic at Motunau
              Beach. He had a mare named Bell in the shafts and a gelding named Walleye in the lead.....George
              changed the pace of the horses for the journey home, putting Bell in the lead. My sister Margaret,
              then six years and five months old, said George was playing an accordion and driving the horses at
              the same time and it seemed to her that at the first pipe in the hollow on the Motunau side of the
              bottom of Bushy Guy cutting the dray just ran over the bank and capsized. My sister Fanny was just
              over two months old, and was tipped out among the others, but was unhurt. It was decided not to
              tell Mrs Gibb of the accident."
            </p>
          </blockquote>

          <p>
            In 1892 George and Stewart were partners in a Government leasehold section of about 1000 ha,
            north of the head of the Greta Stream. When they sold at the turn of the century George kept the
            freehold land they had from Greta Peaks <sup>23</sup>.
          </p>

          {/* Images 161 & 162: Craigknock */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            <ArchivalImageCard
              src="/images/161 Craigknock.jpg"
              alt="Craigknock"
              caption="Craigknock"
              filenameHint="161 Craigknock.jpg"
            />
            <ArchivalImageCard
              src="/images/162 Craigknock is still in Gibb management.jpg"
              alt="Craigknock is still in Gibb management"
              caption="Craigknock, still in Gibb Management"
              filenameHint="162 Craigknock is still in Gibb management.jpg"
            />
          </div>

          <p>
            Craigknock
          </p>

          <p>
            Craigknock, still in Gibb Management
          </p>

          <p>
            Wool prices were very high in the 1899-1900 season owing to the Boer War, so George Gibb spent
            some of that bonus on a trip to the 1900 Paris Exhibition and to Scotland to see his Lindsay relatives,
            and the McAdam parents of sons who were working in the Hurunui area. Edward Roberts took his
            place at Cabbage Tree Flat while he was away, taking his dog Scot to bring in the cows for milking
            without making them run. Edward wrote: "It was not everyone who could get along with his mother
            and she desired me to go and assist in running the farm and attending to travellers' horses."
          </p>

          <p>
            There was a send-off at the Greta Valley School for George before he sailed away. Edward (as he had
            done at other social events) recited Tom Bracken's poem 'How Von Temsky Fell."<sup>24</sup> Polish-born
            Major Gustav Ferdinand von Tempsky was a leader in the bush fighting corps Forest Rangers in the
            NZ Wars. He was a famed bowie knife fighter, using skills gained on the Californian goldfields.
          </p>

          <p>
            When George married, at the age of 48 (and to the relief of his relatives) an English nurse, Alice Anna
            Burgess (1872-1962) on 8 October 1913, nine months before his mother die, his uncle Rev George
            Lindsay, performed the marriage service.
          </p>

          <p>
            They had two sons.
          </p>

          <div className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif space-y-2 text-base sm:text-lg">
            <p>• <strong>David Stewart Gibb</strong> (1914-1980), awarded DFC, married Florence Elisabeth Kent-Johnstone</p>
            <p>• <strong>Eion George Gibb</strong> (1915-1990) married Margaret Helen (1919-2011) and farmed at Craigknock</p>
          </div>

          {/* Footnotes 22 through 24 */}
          <div className="space-y-1.5 p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-4">
            <p><sup>22</sup> George Gibb 1865-1949</p>
            <p><sup>23</sup> That land was sold in 1910</p>
            <p><sup>24</sup> Bracken (1843-92), Irish-born, wrote the words for NZ's National Anthem</p>
          </div>
        </section>

        {/* Section 6: Ella (Helen Isabella) Gibb */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h3 className="font-serif text-xl md:text-2xl font-bold text-[#7a5b3a]">
            ELLA (HELEN ISABELLA) GIBB, later Hoban (1867-1926)
          </h3>

          <p>
            Ella was born with a hearing disability. She lived at Cabbage tree Fat until her mother died in 1914,
            working in the accommodation house preparing meals and rooms for the travelling drovers and
            members of the public. She took part in the extended family life and was well liked by the younger
            members of the family.
          </p>

          <h4 className="font-serif text-lg font-bold text-[#2c221e]">
            Glimpses of Ella from "A Personal History" by Edward Roberts, written in 19609
          </h4>

          <blockquote className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif italic text-base sm:text-lg text-[#2c221e] shadow-xs space-y-4">
            <p>
              In the cold winter of 1886, a fall of snow was followed by hard frosts which iced over
              a pond in the shade of a hill and the Roberts boys took Ella for a walk over the ice,
              making sure she did not fall.
            </p>
            <p>
              Ella went to Christchurch to see the future King George V and Queen Mary<sup>25</sup> who toured
              New Zealand as the Duke and Duchess of Cornwall and York, to thank the country for its
              support in the Boer War. North Canterbury was involved in their visit to Christchurch. The
              Cheviot Mounted Rifles went to take part in a parade, and on the way many stayed a night
              at the Gibb Accommodation House and Ella would have helped host them in comfort. Young
              Ted Roberts was at Cabbage Tree Flat at the time and Lt Jenkins gave him half a crown for
              looking after his horse. Baxter and Mary Roberts and Ella drove to Waipara by horse and gig
              to catch the train to Christchurch, to see the Roya coupe, and returned the same day - it
              was a big day out. On the night train returning home an old chap a little the worse for drink
              gave an exhibition of dancing to the passengers.
            </p>
            <p>
              In the autumn of 1901 Helen Gibb went on a rare holiday to Hanmer Springs so Mary
              Roberts went to help Ella in the house, as there were always travellers dropping in for a
              meal or to put up for the night. Ella must have been very organized and capable to deal with
              that as calmly as she reportedly did.
            </p>
            <p>
              She would have been amused by the antics of the pihoihoi (NZ lark) at the breeding season,
              with their delightful behaviour, and for the rest of the year they were plentiful, running
              along in front of people making a cheep-cheep as they ran ahead without any fright. There
              were pairs of wekas established in their own territories all over the flats and hills, tapping
              things at night and often making away with small objects, and making their coo-et calls in
              the early evening. Pukekos were in the swampy areas.
            </p>
            <p>
              Ella received her early education from her mother, in the evenings, and when her university-
              educated Uncle George was living with them, he did a lot of the teaching.
            </p>
            <p>
              For many years the Gibb and Arkle families were the only people living in the area, although
              many passed through, so Ella would have met a lot of people but had the constancy of
              companionship and friendship with the Arkles.
            </p>
            <p>
              Ella would have bought much of her sewing materials, drapery and manchester from the
              several travelling drapers who served what was then relatively remote north Canterbury.
              They used pack horses before the inland roads were formed.
            </p>
          </blockquote>

          <div className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif italic text-base sm:text-lg text-[#2c221e] shadow-xs">
            <p>"To widow Gibb's beside the way, that once gave sup to all;</p>
            <p>Whom few of all the early school would pass without a call."</p>
            <p className="not-italic text-xs font-bold uppercase tracking-wider text-[#7a5b3a] pt-2">From "A Trip to Cheviot" by T.E.L Roberts</p>
          </div>

          <p>
            Helen Gibb died 28 July 1914. In her will she left specific instructions regarding Ella. The boys were
            left property, then Ella was mentioned.
          </p>

          {/* Footnote 25 */}
          <div className="p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-4">
            <p><sup>25</sup> King George reigned 1910-1936, after his King Edward 8th</p>
          </div>

          {/* Image 163: Helen Gibb's will */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/163 Helen Gibb_s will.jpg"
              alt="Helen Gibb's will"
              caption="Helen Gibb's will"
              filenameHint="163 Helen Gibb_s will.jpg"
            />
          </div>

          <p>
            Later that same year, 1914, Ella married Austin Hoban from a North Canterbury farming family - she
            was 47, and Austin was 42. They went to Southland and farmed at Kapuka, on the Southern Scenic
            Route, about 20 km east of Invercargill.
          </p>

          <p>
            Ella's death certificate states that she had one adopted son, Walter Amelius Hoban, who was born in
            1905, so he was only 20 when Ella died. He became an engineer, worked on the Roxburgh Hydro
            dam, married Jessie Elizabeth (died 1994) and they had one daughter, Judith Ann. Walter and Jessie
            lived out their retirement at 159 Scotland Street, Roxburgh. Walter died at Roxburgh 18 February
            1994.
          </p>

          <p>
            Ella died in Southland Hospital on 22 April 1926, of kidney failure (uraemia cystitis) and she is buried
            in the Eastern Cemetery in Invercargill, alongside Austin who died in 1934, aged 62.
          </p>

          <p>
            It is rather nice to think that Ella, after devoting herself to her mother and family, working hard in the
            Accommodation House business, was able to enjoy her own family life in Southland. By adopting a
            son she would have taken part in rural school and community events, despite her deafness but it is
            likely she would have got a hearing aid, as her uncle James Gibb did. Ella's kidney failure would have
            caused progressive weakness, easy fatigue and many other symptoms which now would be treated
            by dialysis.
          </p>

          {/* Image 164: Cabbage Tree Flat Accommodation House */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/164 Cabbage Tree Flat Accommodation House.jpg"
              alt="Cabbage Tree Flat Accommodation House"
              caption="Photo: from 'Motunau or The Hills of Home' by T.E.L. Roberts"
              filenameHint="164 Cabbage Tree Flat Accommodation House.jpg"
            />
          </div>

          <p>
            Photo: from "Motunau or The Hills of Home" by T.E.L. Roberts
          </p>
        </section>

        {/* Section 7: Lindsay Family Members */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            LINDSAY FAMILY MEMBERS FROM GLEN CLOVA CAME TO LIVE AND TO VISIT
          </h2>

          <div className="space-y-6">
            <div className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif space-y-2">
              <p className="font-bold text-lg text-[#2c221e]">
                1. THE REV GEORGE LINDSAY MADE NEW ZEALAND HIS HOME - see following story
              </p>
            </div>

            <div className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif space-y-2">
              <p className="font-bold text-lg text-[#2c221e]">
                2. CHARLES LINDSAY (born 1841) prospered with an ironmongery business in Glasgow.
              </p>
              <p>
                In early 1892 Charles and his son George came out to NZ for health reasons, in a sailing ship. He
                stayed with Helen and his health improved and he returned to Scotland in a steam ship.
                Charles returned in early 1907 with his second son, Robert. He made his third visit in later
                life with his wife.
              </p>
            </div>

            <div className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif space-y-2">
              <p className="font-bold text-lg text-[#2c221e]">
                3. ANNE FORTESCUE LINDSAY HAZELTON ARRIVED NZ, 25 JUNE 1875 ON BOARD THE 'WAIMATE'
              </p>
              <p>
                Helen's younger sister Anne Fortescue Hazelton lived in the Waimate area, South Canterbury. She
                married Irish-born farm labourer John Hazelton and they arrived with their daughter Jane Lucy<sup>26</sup>.
                Their 10-month-old baby Annie died on the ship coming out. Their NZ-born children were:
              </p>
              <ul className="list-disc list-inside space-y-1.5 pt-2">
                <li><strong>Robert John Charles</strong> (1875-1902) - known as Jack, he stayed with his aunt Helen Gibb from time to time. He went into the army as a gunner and was accidentally killed when he fell over a cliff at Camp Bay while stationed at Lyttelton on 15 April 1902.</li>
                <li><strong>George William</strong> (1877-1931)</li>
                <li><strong>Helen</strong> (born 1879)</li>
                <li><strong>Thomas Lindsay</strong> (1884-1916, killed at the Battle of the Somme, France in WW1)</li>
                <li><strong>Theresa Clementine</strong> (1887-1979)</li>
              </ul>
            </div>
          </div>

          {/* Footnote 26 */}
          <div className="p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-4">
            <p><sup>26</sup> Jane Lucy was born 1871 in Liverpool, died 1891 in Waimate.</p>
          </div>
        </section>

        {/* Section 8: Ashleigh Lewis */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            REMEMBER ASHLEIGH LEWIS WHO RODE HELEN'S PIGS?
          </h2>

          <p>
            As a boy he worked for Helen Gibb at Cabbage Tree Flat, and later worked on Motunau Station. In
            the early summer of 1901, he became the subject or national concern and fears he was lost at sea,
            along with two other local men. They were down at Motunau beach on a Sunday morning and as it
            was a lovely day, they wanted to go out in a rowboat but Ashleigh was the only one who could row
            so they left with one pair of oars. He was wearing a new pair of boots but they were hurting him so
            he took them off and threw them onto the riverbank. Off they set out, down the river and Ashleigh
            thought he would give his passengers a thrill. He rowed out further where there was some roll on
            the ocean, but a wave caught them and took them out of control. They were soon drifting further
            out to sea in a north easterly direction and Ashleigh couldn't row the boat back to shore.
          </p>

          <p>
            The wind got stronger, the sea got rougher and they were clearly in danger. James Hay, who worked
            at Happy Valley, and who had been with the men earlier raised the alarm and a rider went to Tipapa
            to ring Lyttelton for a tug to come and search for the men. It went out but found no sign of the boat
            and returned to base. Ashleigh's mother in Christchurch was told there was little hope that the
            castaways would survive the wild seas. The district mourned, and the news saddened New Zealand.
          </p>

          <p>
            Meanwhile, out in the small boat, Ashleigh's feet were badly blistered from gripping the bottom of
            the boat in with his bare feet. They had no food, and no water but they did have a small can they
            used to bale out the wave water as it broke over them while one man baled and the other prayed.
            They drifted all through Sunday and Monday - by Monday night they were off the Kaikoura Heads
            when another boat passed them but they had no way of attracting the attention of the larger boat.
            Herbert Acton-Adams from Tipapa was on that boat and he said the trip was so rough that the
            breakfast table was empty in the morning.
          </p>

          <p>
            As Tom Roberts wrote later. "How any human being could stick it through such an ordeal and bring
            that boat, is beyond the imagination of anyone knowing the difficulties of such a circumstance. For
            one pair of hands to hold continuously to the oars for 48 hours in such a sea, where one side-on
            wave would swamp the boat, was at least worthy of a recognition, the writer is afraid, Ashleigh did
            not receive. On Tuesday the wind changed to an easterly, which immediately smoothed out the sea
            and helped them back inshore, where they landed just north of the Jed on Cheviot. When they
            actually touched ashore Ashleigh was unable to walk and commenced to crawl to a home of two
            bachelors of the name Wilson.... They had not proceeded far when an elderly man, and his daughter,
            of the name Welsh came to their assistance and the lady carried Ashleigh into quarters."<sup>27</sup>
          </p>

          <p>
            The others were tired from exposure but Asheigh was crippled for many months and he had scars on
            his feet for the rest of his life.
          </p>

          <p>
            Ashleigh retired to Rotheram. Tom Roberts said he was as lithesome as a boy when he was in his
            70's, and still rode horses for exercise and pleasure. The two men talked about their pig-riding
            adventures at Cabbage Tree Flat - as it is so often that we remember so vividly those exciting little
            adventures from childhood.
          </p>

          {/* Footnote 27 */}
          <div className="p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-4">
            <p><sup>27</sup> "Motunau of The Hills of Home" by T.E.L. Roberts, pp137-8</p>
          </div>
        </section>

        {/* Section 9: The Rev George Lindsay */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            THE REV GEORGE LINDSAY, Helen Gibb's younger brother, came to NZ with the Free Church of Scotland
          </h2>

          <p className="font-serif text-lg font-semibold text-[#7a5b3a]">
            From the Lindsay Clan homeland in the Angus Glens to the new settlements in the lower South Island - the man
            at the manse who had a murder in his community and who ministered to a convicted murderess before she was
            hanged
          </p>

          {/* Image 164A: Rev George Lindsay */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/164A Rev George Lindsay.jpg"
              alt="Rev George Lindsay"
              caption="Rev George Lindsay (1846-1933)"
              filenameHint="164A Rev George Lindsay.jpg"
            />
          </div>

          <blockquote className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif italic text-base sm:text-lg text-[#2c221e] shadow-xs">
            <p>
              "... loved for his faithful service, warm sympathy with the sick and sorrowing, and above all, to the fact that he
              was never heard to speak disparagingly of anyone."
            </p>
          </blockquote>

          <p>
            George<sup>28</sup> grew up on a farm at Glen Clova but must have shown early aptitude for learning. His parents must
            have encouraged him and paid for his studies in Glasgow where he first studied liberal arts and then at
            Glasgow Free Church College. He spoke and read Hebrew, Greek and Latin.
          </p>

          <p>
            He was appointed to the Canterbury District by the Colonial Committee of the Free Church of Scotland in March
            1873. His first position was as a probationer at Waihola, a small community on the edge of a lake south of
            Dunedin, where he served for three years.
          </p>

          <p>
            He was ordained in Waimate in 1876, and two years later married Jessie Mollinson, and as the picture below
            shows, they had a large manse to live in. His sister Anne Fortescue Lindsay and her Irish husband John came to
            Port Chalmers on the Waimate early 1875, and they settled in the Waimate area.
          </p>

          {/* Footnote 28 */}
          <div className="p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-4">
            <p><sup>28</sup> Born Glen Clova 12 Feb 1846 Died Christchurch 27 August 1933</p>
          </div>

          <p>
            His next position was at Otepopo (Herbert) 1882-1889, then a busy settlement with a large hinterland of
            rolling hills and rich farmland poised to benefit from new technology that would bring wealth and people to
            this part of North Otago.
          </p>

          <p>
            Some significant events occurred during their tenure of the manse:
          </p>

          <ul className="list-disc list-inside space-y-2 p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif">
            <li>The first shipment of frozen meat was sent from Totara Estate (not far north of Herbert) to the UK in 1882, making farming communities more prosperous.</li>
            <li>The Church replaced the long run Australian hardwood timber roof with an iron roof.</li>
            <li>The Otago Provincial Government made a determined effort "to eradicate and to destroy before blooming and to cut, eradicate and destroy every thistle visible above the ground for a period of two years," and let a large contract in the Otepopo area to ensure this happened. The Thistle quickly became an invasive and costly weed after being introduced by Scottish settlers.</li>
            <li>Their four-year-old son George Ewing died at the manse in 1885.</li>
          </ul>

          <h3 className="font-serif text-xl md:text-2xl font-bold text-[#2c221e] pt-4">
            There was a murder at the Imperial Hotel
          </h3>

          <p className="font-serif font-bold text-lg text-[#7a5b3a]">
            FEARFUL TRAGEDY AT OTEPOPO (June 1885) was the headline
          </p>

          <p>
            Henry Dixon White (25), son of shopkeeper James White, went into the bar parlour of the Imperial Hotel in
            Herbert, pulled out a revolver and shot 10-year-old Ada Paterson<sup>29</sup>, daughter of the hotel owners, and then
            shot himself. He left two letters, one explaining his actions and the other leaving several hundred pounds to
            Ada's parents, James and Mary Paterson.
          </p>

          <blockquote className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif italic text-base sm:text-lg text-[#2c221e] shadow-xs">
            <p>
              "Ada was too generous and kind of nature, too impulsive to sympathy, and unselfish, too sensitive to the
              highest emotions to be anything but unhappy in this world," he wrote.
            </p>
          </blockquote>

          <p>
            Henry was known as a local eccentric but considered harmless and with a gentle disposition towards children.
            He wore ribbons in his hair, in the style of the decorations in a horse's mane. His fondness for young Ada was
            well known, who was a beautiful child and Henry spent a great deal of time at the Imperial sitting watching
            and admiring her. The jury took 20 minutes to decide he was of unsound mind at the time.
          </p>

          <p>
            St Paul's Invercargill was their home for 22 years, 1889-1911 - "an effective and winning preacher and
            beloved pastor."
          </p>

          <p>
            While he was at the heart of the large congregation for more than two decades, it was for one woman that he
            came to national attention. In 1895 Minnie Dean was hanged for murdering young children in her care. It was a
            case of great public interest, hostility, passion and judgement, and the distance of time probably takes a wider
            view of the case, as explained by author Lynley Hood.
          </p>

          <p>
            It was George Lindsay who ministered to her in prison, although reportedly he was initially reluctant to do so.
            She told him that as far as the evidence was concerned the sentence was justified, but she protested her
            innocence as regards intention and forethought. His role has been dramatized in fictional works on the subject.
            He was under pressure to reject Minnie Dean, and the trial reactivated his wife's grief over the death of their
            young George in 1885. It must have been a difficult time for him. Like him, Minnie was born in Scotland, at
            Greenock. Unlike him, she did not live well in the respect and love of the wider community.
          </p>

          <div className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] space-y-3 font-serif">
            <p className="font-bold text-sm text-[#7a5b3a] uppercase tracking-wider">The local paper reported on 2 Feb 1911:</p>
            <h4 className="font-bold text-lg text-[#2c221e]">The Rev G Lindsay — IMPRESSIVE FAREWELL</h4>
            <div className="italic text-base sm:text-lg space-y-3 leading-relaxed">
              <p>
                "A public meeting was held in St Paul's Presbyterian Church this evening to bid farewell to the Rev George
                Lindsay and Mrs Lindsay who are moving to St Clair. Mr Lindsay has a splendid record of history behind him. He
                was ordained so long ago.... Over 22 years ago Mr Lindsay took charge of the then-recently established St
                Paul's Church. Under his ministration the congregation has grown to be one of the most powerful in the colony.
                It is not, therefore, surprising that the meeting was large and cordial. The church was filled to the doors, not
                only be members of St Paul's and other Presbyterian Churches in and about Invercargill, but also by members of
                all the other protestant congregations in the town.
              </p>
              <p>
                The affection of St Paul's congregation for their departing minister and his wife took a very tangible form. Mrs
                Lindsay was presented with a handsome dressing case ad a valuable muff chain, and a purse of sovereigns.
                On making the presentation to Mr Lindsay Mr J.S. Baxter remarked that he was worth his weight in gold."
              </p>
            </div>
          </div>

          <p>
            George was presented with 160 Pounds of sovereigns presented in a bag and the sentiment that he took the
            heartfelt love of the congregation with him. In responding George said he was considerably moved, as always
            by the sense of kindness in his congregation. He said he did not undervalue money as it possibly increased one's
            power of helping others, but that the acquisition of money had never been his object. He was satisfied that the
            way of service was by sacrifice.
          </p>

          <p>
            He said it was hard, at the end of 22 years, to tear up the old associations by the roots. He had shared in the
            joys, sorrows and lives of the congregation and it was now a severe wrench to leave but he felt the step he was
            taking was in the path of duty.
          </p>

          <p>
            Thus, in his mid-60's he moved to the smaller parish of St Clair Dunedin 1911-1913.
          </p>

          <p>
            In 1911 he was Moderator for the Presbyterian Church General Assembly.
          </p>

          <p>
            Southbridge was his final posting, 5 June 1913 until his retirement on 30 April 1918, aged 72. A few years
            later his great-niece Annie Denton would be a member of the Southbridge congregation, attending services
            from Oakdale.
          </p>

          <p>
            George married Jessie Fawns Mollison (1858-1950) on 5 June 1878.
          </p>

          <p>
            Jessie's parents, Alexander and Elizabeth (nee Forsyth), had married in Angus (Scotland) and come to Dunedin
            where Jessie, the eldest of six, was born. The name Fawns comes from her paternal grandmother, Janet Fawns.
          </p>

          <p>
            George and Jessie had six children:
          </p>

          <div className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif space-y-2 text-base sm:text-lg">
            <p>• <strong>Alexander Mollison Lindsay</strong> (born 1879) qualified as a marine engineer in 1904.</p>
            <p>• <strong>George Ewing Lindsay</strong> (1881-85)</p>
            <p>• <strong>Ernest Charles Lindsay CBE</strong> (1883-1943) studied science at Otago University then did medical training at London Hospital, where he played full back in their rugby football XV. We won a scholarship to the medical college. During WW1 he worked in field ambulances and as a surgeon. He was General Surgeon although he suffered from hydatid of the liver which he probably got in NZ. (Details in Plarr's Lives of the Fellows, the Royal College of Surgeons of England.)</p>
            <p>• <strong>Andrew Alexander Bonar Lindsay</strong> (1885-1970) was one of the first "kilted kiwis" - NZ-born of Scottish parents who were eligible to play for the Scotland National Rugby Union Team. He played two tests in 1910-11.</p>
            <p>• <strong>Euphemia McKellar Lindsay</strong> (1890-1961) married William Abraham Studholme; they farmed at Wanaka</p>
            <p>• <strong>Duncan Brown Lindsay</strong> (1893-4 June 1943) married Eveline Elizabeth Lory in 1920. He was killed in the Hyde Rail Disaster, aged 50, when the drunk driver was travelling at excessive speed, killing 21 people. Duncan is buried in Ranfurly Cemetery. He lived at Wedderburn, Central Otago.</p>
          </div>

          {/* Image 164B: Hyde rail crash */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/164B Hyde rail crash Photo ODT.jpg"
              alt="Hyde rail crash Photo ODT"
              caption="The Hyde crash on the Otago Central Line (now the Otago Rail Trail) (Photo: ODT)"
              filenameHint="164B Hyde rail crash Photo ODT.jpg"
            />
          </div>

          <p>
            The Hyde crash on the Otago Central Line (now the Otago Rail Trail) (Photo: ODT)
          </p>

          <p>
            Relating to the Herbert murder.....
          </p>

          <p>
            The Imperial Hotel, on the main road through Herbert and the corner of Chanonry Street (Photo: J W Allen,
            1875)- Presbyterian Church on far right; store opposite hotel is still there
          </p>

          {/* Image 164C: Ida's headstone */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/164C Ida_s headstone at Herbert Cemetery.jpg"
              alt="Ida's headstone at Herbert Cemetery"
              caption="The Paterson headstone at Herbert Cemetery atop Mt Charles, remembering young Ida and her parents"
              filenameHint="164C Ida_s headstone at Herbert Cemetery.jpg"
            />
          </div>

          <p>
            The Paterson headstone at Herbert Cemetery atop Mt Charles, remembering young Ida and her parents
          </p>

          {/* Footnote 29 */}
          <div className="p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-4">
            <p><sup>29</sup> The newspaper reports named her Ada but her headstone names her as Ida.</p>
          </div>
        </section>

        {/* Navigation / Return to Page 1 */}
        <div className="pt-8 border-t-2 border-[#d8ceb9] flex flex-wrap items-center justify-between gap-4">
          <button
            onClick={() => onSelectPage && onSelectPage('page1')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#e8dec9] hover:bg-[#d8ceb9] text-[#7a5b3a] font-serif font-bold text-sm transition cursor-pointer"
          >
            <span>&larr; Page 1: Our Highland Connection</span>
          </button>

          <button
            onClick={() => onSelectPage && onSelectPage('hub')}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white font-serif font-bold text-sm transition shadow-sm cursor-pointer"
          >
            <Home className="w-4 h-4" />
            <span>Gibb Family Overview</span>
          </button>
        </div>

      </article>
    </div>
  );
};
